import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Clock, Wallet, CreditCard, Building2, Calendar, Hash, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PendingPaymentsManagement = () => {
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showValidateDialog, setShowValidateDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPendingPayments();
  }, []);

  const fetchPendingPayments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/pending-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingPayments(response.data);
    } catch (error) {
      console.error('Error fetching pending payments:', error);
      toast.error('Erreur lors du chargement des paiements');
    } finally {
      setLoading(false);
    }
  };

  const handleValidatePayment = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      
      // Utiliser le bon endpoint selon le type de paiement
      const endpoint = selectedPayment.payment_type === 'OPTIONAL_MODULE'
        ? `${API}/super-admin/module-subscription-payments/${selectedPayment.id}/validate`
        : `${API}/super-admin/validate-payment/${selectedPayment.id}`;
      
      await axios.post(
        endpoint,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Paiement validé avec succès!');
      setShowValidateDialog(false);
      setSelectedPayment(null);
      fetchPendingPayments();
    } catch (error) {
      console.error('Error validating payment:', error);
      toast.error('Erreur lors de la validation');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectPayment = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Veuillez spécifier une raison de rejet');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      
      // Utiliser le bon endpoint selon le type de paiement
      const endpoint = selectedPayment.payment_type === 'OPTIONAL_MODULE'
        ? `${API}/super-admin/module-subscription-payments/${selectedPayment.id}/reject?rejection_reason=${encodeURIComponent(rejectionReason)}`
        : `${API}/super-admin/reject-payment/${selectedPayment.id}?rejection_reason=${encodeURIComponent(rejectionReason)}`;
      
      await axios.post(
        endpoint,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Paiement rejeté');
      setShowRejectDialog(false);
      setSelectedPayment(null);
      setRejectionReason('');
      fetchPendingPayments();
    } catch (error) {
      console.error('Error rejecting payment:', error);
      toast.error('Erreur lors du rejet');
    } finally {
      setProcessing(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-GN').format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPaymentMethodIcon = (method) => {
    switch(method) {
      case 'CASH':
        return <Wallet className="h-5 w-5 text-green-600" />;
      case 'CHEQUE':
        return <CreditCard className="h-5 w-5 text-blue-600" />;
      default:
        return null;
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch(method) {
      case 'CASH':
        return 'Cash';
      case 'CHEQUE':
        return 'Chèque';
      default:
        return method;
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600">Chargement des paiements...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="h-5 w-5 mr-2" />
            Paiements en Attente de Validation
            <Badge variant="outline" className="ml-auto">
              {pendingPayments.length} paiement{pendingPayments.length !== 1 ? 's' : ''}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingPayments.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 mx-auto text-green-600 mb-3" />
              <p className="text-gray-600">Aucun paiement en attente</p>
              <p className="text-sm text-gray-500 mt-1">Tous les paiements ont été traités</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingPayments.map((payment) => (
                <Card key={payment.id} className="border-2 border-yellow-200">
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          {getPaymentMethodIcon(payment.payment_method)}
                          <div>
                            <h3 className="font-bold text-lg">{payment.tenant_name}</h3>
                            <p className="text-sm text-gray-600">{payment.invoice_description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-blue-600">
                            {formatPrice(payment.amount)} GNF
                          </div>
                          <Badge variant="outline" className="mt-1">
                            {getPaymentMethodLabel(payment.payment_method)}
                          </Badge>
                        </div>
                      </div>

                      {/* Payment Details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                        {payment.payment_method === 'CASH' ? (
                          <>
                            <div className="flex items-start space-x-2">
                              <Calendar className="h-4 w-4 text-gray-600 mt-0.5" />
                              <div>
                                <p className="text-xs text-gray-500">Date de paiement</p>
                                <p className="font-medium">{payment.payment_date}</p>
                              </div>
                            </div>
                            <div className="flex items-start space-x-2">
                              <Hash className="h-4 w-4 text-gray-600 mt-0.5" />
                              <div>
                                <p className="text-xs text-gray-500">Référence du reçu</p>
                                <p className="font-medium">{payment.receipt_reference}</p>
                              </div>
                            </div>
                            {payment.notes && (
                              <div className="col-span-2">
                                <p className="text-xs text-gray-500">Notes</p>
                                <p className="text-sm">{payment.notes}</p>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="flex items-start space-x-2">
                              <Hash className="h-4 w-4 text-gray-600 mt-0.5" />
                              <div>
                                <p className="text-xs text-gray-500">Numéro de chèque</p>
                                <p className="font-medium">{payment.cheque_number}</p>
                              </div>
                            </div>
                            <div className="flex items-start space-x-2">
                              <Building2 className="h-4 w-4 text-gray-600 mt-0.5" />
                              <div>
                                <p className="text-xs text-gray-500">Banque</p>
                                <p className="font-medium">{payment.bank_name}</p>
                              </div>
                            </div>
                            <div className="flex items-start space-x-2">
                              <Calendar className="h-4 w-4 text-gray-600 mt-0.5" />
                              <div>
                                <p className="text-xs text-gray-500">Date du chèque</p>
                                <p className="font-medium">{payment.cheque_date}</p>
                              </div>
                            </div>
                            {payment.notes && (
                              <div className="col-span-2">
                                <p className="text-xs text-gray-500">Notes</p>
                                <p className="text-sm">{payment.notes}</p>
                              </div>
                            )}
                          </>
                        )}
                        <div className="flex items-start space-x-2 col-span-2">
                          <Clock className="h-4 w-4 text-gray-600 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-500">Soumis le</p>
                            <p className="text-sm">{formatDate(payment.submitted_at)}</p>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex justify-end space-x-3 pt-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowRejectDialog(true);
                          }}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Rejeter
                        </Button>
                        <Button
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowValidateDialog(true);
                          }}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Valider
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Validate Dialog */}
      <Dialog open={showValidateDialog} onOpenChange={setShowValidateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
              Valider le Paiement
            </DialogTitle>
            <DialogDescription>
              Confirmez-vous la validation de ce paiement?
            </DialogDescription>
          </DialogHeader>
          {selectedPayment && (
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p className="text-sm"><strong>Établissement:</strong> {selectedPayment.tenant_name}</p>
                <p className="text-sm"><strong>Montant:</strong> {formatPrice(selectedPayment.amount)} GNF</p>
                <p className="text-sm"><strong>Méthode:</strong> {getPaymentMethodLabel(selectedPayment.payment_method)}</p>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <p className="text-sm text-green-800">
                    {selectedPayment.payment_type === 'OPTIONAL_MODULE'
                      ? "La validation activera automatiquement le module optionnel pour l'établissement."
                      : "La validation activera automatiquement l'accès de l'établissement aux modules de base."}
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowValidateDialog(false)}
                  disabled={processing}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleValidatePayment}
                  disabled={processing}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {processing ? 'Validation...' : 'Confirmer la Validation'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <XCircle className="h-5 w-5 mr-2 text-red-600" />
              Rejeter le Paiement
            </DialogTitle>
            <DialogDescription>
              Veuillez spécifier la raison du rejet.
            </DialogDescription>
          </DialogHeader>
          {selectedPayment && (
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p className="text-sm"><strong>Établissement:</strong> {selectedPayment.tenant_name}</p>
                <p className="text-sm"><strong>Montant:</strong> {formatPrice(selectedPayment.amount)} GNF</p>
                <p className="text-sm"><strong>Méthode:</strong> {getPaymentMethodLabel(selectedPayment.payment_method)}</p>
              </div>

              <div>
                <Label htmlFor="rejection_reason">Raison du Rejet *</Label>
                <Input
                  id="rejection_reason"
                  type="text"
                  placeholder="Ex: Montant incorrect, chèque sans provision, etc."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="mt-2"
                />
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  <p className="text-sm text-red-800">
                    L'établissement recevra une notification avec la raison du rejet.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectDialog(false);
                    setRejectionReason('');
                  }}
                  disabled={processing}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleRejectPayment}
                  disabled={processing || !rejectionReason.trim()}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {processing ? 'Rejet...' : 'Confirmer le Rejet'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PendingPaymentsManagement;
